import React from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Star, ArrowRight, User, Building2 } from 'lucide-react'
import DataResultsContainer from './DataResultsContainer.tsx'
import styles from './ConversationalMode.module.scss'

interface Professional {
  id: string | number
  type?: 'PERSON' | 'COMPANY'
  name?: string
  last_name?: string
  razon_social?: string
  validated?: boolean
  image_profile?: string
  zone?: string
  stars?: number
  skill?: Array<{ skill?: { name?: string } }>
  skills?: Array<{ name?: string }>
  source?: 'profiles' | 'workers'
  displayName?: string
  profileType?: string
}

interface ProfessionalsListProps {
  professionals: Professional[]
  total: number
  noResultsMessage?: string
}

const ProfessionalsList: React.FC<ProfessionalsListProps> = ({ professionals, total, noResultsMessage }) => {
  const navigate = useNavigate()

  const getSkillLabels = (professional: Professional): string[] => {
    const fromSkill = (professional.skill ?? [])
      .map((item) => item?.skill?.name)
      .filter(Boolean) as string[]
    
    const fromSkills = (professional.skills ?? [])
      .map((item) => item?.name)
      .filter(Boolean) as string[]

    return Array.from(new Set([...fromSkill, ...fromSkills])).slice(0, 2)
  }

  const getDisplayName = (professional: Professional): string => {
    if (professional.displayName) return professional.displayName
    if (professional.type === 'COMPANY') return professional.razon_social || 'Empresa'
    return `${professional.name || ''} ${professional.last_name || ''}`.trim() || 'Profesional'
  }

  const getProfileType = (professional: Professional): string => {
    if (professional.profileType) return professional.profileType
    if (professional.source === 'workers') return 'Trabajador'
    if (professional.type === 'COMPANY') return 'Empresa'
    return 'Profesional'
  }

  return (
    <DataResultsContainer
      icon={<User size={18} />}
      title={`${professionals.length} ${professionals.length === 1 ? 'perfil encontrado' : 'perfiles encontrados'}`}
      subtitle={total > 3 ? `de ${total} totales` : undefined}
      color="green"
      total={total}
      maxDisplay={3}
      viewAllLink={`/buscar-perfiles`}
      viewAllText={`Ver los ${total} perfiles en Explorar Perfiles`}
      emptyMessage={noResultsMessage || "No se encontraron profesionales con esos criterios."}
      isEmpty={professionals.length === 0}
    >
      {professionals.slice(0, 3).map((professional) => {
        const displayName = getDisplayName(professional)
        const skillLabels = getSkillLabels(professional)
        const profileType = getProfileType(professional)
        const isCompany = professional.type === 'COMPANY'

        return (
          <div key={`${professional.source}-${professional.id}`} className={`${styles.resultCard} ${styles.resultCardGreen}`}>
            <div className={styles.professionalCardHeader}>
              <div className={styles.professionalCardAvatar}>
                {professional.image_profile ? (
                  <img src={professional.image_profile} alt={displayName} />
                ) : (
                  isCompany ? <Building2 size={18} /> : <User size={18} />
                )}
              </div>
              <div className={styles.professionalCardInfo}>
                <span className={styles.resultCardTitle}>
                  {displayName}
                  {professional.validated && (
                    <span className={styles.verifiedBadge} title="Verificado">✓</span>
                  )}
                </span>
                <span className={`${styles.resultCardBadge} ${styles.resultCardBadgeGreen}`}>{profileType}</span>
              </div>
            </div>

            {skillLabels.length > 0 && (
              <div className={styles.resultCardChips}>
                {skillLabels.map((label) => (
                  <span key={label} className={styles.resultCardChip}>{label}</span>
                ))}
              </div>
            )}

            <div className={styles.resultCardMeta}>
              {professional.zone && (
                <span><MapPin size={12} /> {professional.zone}</span>
              )}
              {professional.stars !== undefined && professional.stars > 0 && (
                <span><Star size={12} /> {professional.stars.toFixed(1)}</span>
              )}
            </div>

            <button
              type="button"
              className={`${styles.resultCardCta} ${styles.resultCardCtaGreen}`}
              onClick={() => navigate(`/perfil/${professional.id}`)}
            >
              Ver perfil <ArrowRight size={14} />
            </button>
          </div>
        )
      })}
    </DataResultsContainer>
  )
}

export default ProfessionalsList
